import type { QueryClient } from '@tanstack/react-query';
import {
  ALL_CONNECTION_QUERY_KEY,
  CONFIG_QUERY_KEY,
  DEPLOYED_VOICES_QUERY_KEY,
  DISCLAIMER_QUERY_KEY,
  FLOWS_QUERY_KEY,
  FUNCTIONS_QUERY_KEY,
  HANDOFFS_QUERY_KEY,
  KNOWLEDGE_BASE_QUERY_KEY,
  PRONUNCIATION_QUERY_KEY,
  SMS_CREDENTIALS_QUERY_KEY,
  SMS_TEMPLATES_QUERY_KEY,
  TELEPHONE_MUTATION_KEY,
  TOPICS_QUERY_KEY,
  VARIANT_MANAGEMENT_QUERY_KEY,
  VOICE_TUNING_MUTATION_KEY,
} from 'constants/QueryKeys.constants';
import { v4 as uuidv4 } from 'uuid';

import type { DeployedVoice } from 'api/resources/deployedVoice/types';
import type { ProjectVersionState } from 'api/resources/deployments/types';
import type { VariantManagementResponse } from 'api/resources/variantManagement/types';
import type { AssistantVoiceOption } from 'api/types';
import {
  ACTIVE_QUERY_KEY,
  DEPLOYMENTS_MUTATION_KEY,
} from 'hooks/api/deployments/hooks';
import { transformDisclaimerData } from 'hooks/useDeployments/utils';
import {
  ALL_ENVIRONMENTS_QUERY_KEY,
  PHONE_NUMBER_MUTATION_KEY,
} from 'hooks/usePhoneNumber';
import { getProjectQueryKey } from './query';

export const setVersionState = ({
  accountId,
  projectId,
  queryClient,
  versionStateData,
  mockData = false,
  voiceOptions = undefined,
}: {
  accountId: string;
  projectId: string;
  queryClient: QueryClient;
  versionStateData: ProjectVersionState;
  mockData?: boolean;
  voiceOptions?: AssistantVoiceOption[];
}) => {
  queryClient.setQueryData(getProjectQueryKey(accountId, projectId), {
    name: versionStateData.name,
    id: projectId,
    created_at: versionStateData.created_at,
    active: true,
    genai_account_id: accountId,
    is_draft: false,
    token_limit_exceeded: false,
    type: undefined,
  });

  queryClient.setQueryData(
    [accountId, projectId, CONFIG_QUERY_KEY],
    versionStateData.config,
  );

  queryClient.setQueryData([accountId, projectId, PRONUNCIATION_QUERY_KEY], {
    pronunciations: versionStateData.config.tts_rules,
  });

  if (Array.isArray(versionStateData.functions)) {
    queryClient.setQueryData([accountId, projectId, FUNCTIONS_QUERY_KEY], {
      functions: versionStateData.functions,
      active_functions: versionStateData.functions,
    });
    versionStateData.functions?.forEach(func => {
      queryClient.setQueryData(
        [accountId, projectId, FUNCTIONS_QUERY_KEY, func.function_id],
        func,
      );
    });
  } else {
    queryClient.setQueryData(
      [accountId, projectId, FUNCTIONS_QUERY_KEY],
      versionStateData.functions,
    );
    versionStateData.functions?.active_functions.forEach(func => {
      queryClient.setQueryData(
        [accountId, projectId, FUNCTIONS_QUERY_KEY, func.function_id],
        func,
      );
    });
  }

  if (Array.isArray(versionStateData.handoffs)) {
    queryClient.setQueryData([accountId, projectId, HANDOFFS_QUERY_KEY], {
      handoffs: versionStateData.handoffs,
      active_handoffs: versionStateData.handoffs,
    });
  } else {
    queryClient.setQueryData(
      [accountId, projectId, HANDOFFS_QUERY_KEY],
      versionStateData.handoffs,
    );
  }

  queryClient.setQueryData(
    [accountId, projectId, KNOWLEDGE_BASE_QUERY_KEY],
    versionStateData.knowledge_base,
  );

  queryClient.setQueryData([accountId, projectId, SMS_CREDENTIALS_QUERY_KEY], {
    account_id: accountId,
    project_id: projectId,
    integration: 'twilio',
  });

  queryClient.setQueryData(
    [accountId, projectId, SMS_TEMPLATES_QUERY_KEY],
    versionStateData.sms_templates,
  );

  queryClient.setQueryData([accountId, projectId, TOPICS_QUERY_KEY], {
    topics: versionStateData.topics,
  });
  queryClient.setQueryData(
    [accountId, projectId, VOICE_TUNING_MUTATION_KEY],
    versionStateData.voice_tuning_settings,
  );

  const disclaimerData = transformDisclaimerData({
    knowledge_base: versionStateData.knowledge_base,
  });

  queryClient.setQueryData(
    [accountId, projectId, DISCLAIMER_QUERY_KEY],
    disclaimerData,
  );

  queryClient.setQueryData(
    [accountId, projectId, FLOWS_QUERY_KEY],
    versionStateData.flows,
  );
  versionStateData.flows.forEach(flow => {
    queryClient.setQueryData(
      [accountId, projectId, FLOWS_QUERY_KEY, flow.id],
      flow,
    );
  });

  queryClient.setQueryData(
    [accountId, projectId, VARIANT_MANAGEMENT_QUERY_KEY],
    {
      variants: versionStateData.variants,
      attributes: versionStateData.variant_attributes,
      values: getVariantManagementValues(versionStateData),
    },
  );

  if (mockData) {
    queryClient.setQueryData([accountId, projectId, TELEPHONE_MUTATION_KEY], {
      connector_id: 'MOCK_CONNECTOR_ID',
      token: 'MOCK_TOKEN',
    });
    queryClient.setQueryData(
      [accountId, projectId, TELEPHONE_MUTATION_KEY, ALL_CONNECTION_QUERY_KEY],
      {
        sandbox: { token: 'MOCK_TOKEN' },
      },
    );
    queryClient.setQueryData(
      [accountId, projectId, DEPLOYMENTS_MUTATION_KEY, ACTIVE_QUERY_KEY],
      {
        live: {
          id: 'MOCK_DEPLOYMENT_ID',
          client_env: 'live',
          deployment_id: 'MOCK_DEPLOYMENT_ID',
        },
      },
    );
  }

  if (versionStateData.phone_numbers) {
    queryClient.setQueryData(
      [
        accountId,
        projectId,
        PHONE_NUMBER_MUTATION_KEY,
        ALL_ENVIRONMENTS_QUERY_KEY,
      ],
      { phone_numbers: versionStateData.phone_numbers },
    );
  }

  if (versionStateData.deployed_voices) {
    const findVoice = (providerVoiceId: string) => {
      return voiceOptions?.find(
        voice => voice.provider_voice_id === providerVoiceId,
      );
    };
    const deployedVoices: DeployedVoice[] =
      versionStateData.deployed_voices?.voices_with_proba?.map(v => ({
        id: uuidv4(),
        voice_id: findVoice(v.voice.provider_voice_id)?.id ?? '',
        probability: Number(v.probability.toFixed(1)),
        genai_project_id: projectId,
      }));
    queryClient.setQueryData(
      [accountId, projectId, DEPLOYED_VOICES_QUERY_KEY],
      deployedVoices,
    );
  }
};

export const getVariantManagementValues = (
  versionState: ProjectVersionState,
) => {
  const variantManagementValues: VariantManagementResponse['values'] = {};
  versionState.variants.forEach(variant => {
    const variantValues: VariantManagementResponse['values'][string] = {};
    versionState.variant_attributes.forEach(value => {
      variantValues[value.id] = value.values[variant.id];
    });
    variantManagementValues[variant.id] = variantValues;
  });
  return variantManagementValues;
};
